import Icon from './Icon';
import StatInfo from './StatInfo';

// Price-source and price-review marks — line badges, list tags, the VAT flag
// and the admin "מחירים אוטומטיים" filter (server: lib/estimatePrice.js,
// lib/invoiceMatch.js, lib/noteVat.js). One file so one concept has one name
// and one explanation everywhere it appears. Built from the design system's
// .badge pill (docs/brain/08-design-system.md) — never inline colours.

// ── One explanation per concept ───────────────────────────────────────────────
const UNMATCHED_INFO = 'החשבונית של החודש הגיעה, אבל לא נמצא בה מחיר ברור לתעודה. המחיר נשאר משוער. כדאי לבדוק ולהזין מחיר.';
const INVOICE_INFO = 'תעודות משלוח שהמחיר בהן נלקח אוטומטית מהחשבונית של החודש.';
const VAT_CHECK_INFO = 'המחיר בתעודה השתנה אוטומטית, והמערכת לא בטוחה שהמע״מ נכון. זה קורה כשלספק מוגדר מע״מ חלקי, כשבתעודה יש מע״מ רק על חלק מהסכום, או כשאין לספק מסמכים קודמים ללמוד מהם. אחרי בדיקה, שמירת המסמך מסירה את הסימון.';
const ESTIMATED_DOC_INFO = 'תעודות שיש בהן מחיר שהושלם אוטומטית מהמחיר האחרון הידוע (משוער) — הוא לא הופיע על התעודה.';
// Per line (the document screen and the upload screen).
const LINE_INVOICE_INFO = 'המחיר נלקח מהחשבונית של החודש, אחרי הנחת השורה — שורה באותה כמות, או המחיר היחיד של המוצר בחשבונית';
const LINE_UNMATCHED_INFO = 'החשבונית של החודש הגיעה, אבל לא נמצא בה מחיר ברור לשורה הזו (לא באותה כמות, ולא מחיר אחד). המחיר נשאר משוער. כדאי לבדוק ולהזין מחיר.';
// Notes before 09/2026 are frozen (issue #187): only newer ones are re-priced
// when the invoice arrives, so the promise names the month it applies from.
const LINE_ESTIMATED_INFO = 'המחיר הושלם אוטומטית מהמחיר האחרון הידוע — לא הופיע על התעודה. בתעודות מ-09/2026 הוא יתעדכן כשתתקבל החשבונית של החודש.';

// Where a delivery/return-note line's price came from (items.price_estimated):
//   2 → "מחיר מהחשבונית": the month's invoice billed this line at this price
//   1 → "משוער": last known price
//   1 + no_invoice_match → the invoice is in but didn't answer this line
//   0 → nothing (the price is on the note, or was typed in)
export default function PriceSourceBadge({ item }) {
  const src = Number(item?.price_estimated) || 0;
  if (src === 2) {
    return <span title={LINE_INVOICE_INFO} className="badge badge-compact badge-green price-source-badge">מחיר מהחשבונית</span>;
  }
  if (src !== 1) return null;
  if (item.no_invoice_match) {
    return <span title={LINE_UNMATCHED_INFO} className="badge badge-compact badge-red price-source-badge">משוער – לא נמצאה התאמה בחשבונית</span>;
  }
  return <span title={LINE_ESTIMATED_INFO} className="badge badge-compact badge-yellow price-source-badge">משוער</span>;
}

// ── The admin "מחירים אוטומטיים" filter ───────────────────────────────────────
// Server: PRICE_SOURCE_FILTERS in routes/documents.js. Labels = the row tags,
// word for word, so a tag seen in the list is found in the filter by the same
// name. Copy approved by the owner 2026-10-05; "כולל משוער" added 2026-10-06 —
// every note with a line still estimated (an unmatched one too, which an
// admin's row shows as the red tag). Picking both review options = everything
// that needs a check.
export const PRICE_SOURCE_FILTER_LABEL = 'מחירים אוטומטיים';
export const PRICE_SOURCE_OPTIONS = [
  { key: 'estimated', label: 'כולל משוער',              info: ESTIMATED_DOC_INFO },
  { key: 'invoice',   label: 'מחיר מהחשבונית',          info: INVOICE_INFO },
  { key: 'unmatched', label: 'לא נמצאה התאמה בחשבונית', info: UNMATCHED_INFO },
  { key: 'vat_check', label: 'לבדוק מע״מ',              info: VAT_CHECK_INFO },
];
const optionOf = (key) => PRICE_SOURCE_OPTIONS.find((o) => o.key === key);
export const priceSourceLabel = (key) => optionOf(key)?.label || key;

// The ⓘ beside each filter option — a tap target (works on a phone), not a title.
export function PriceSourceInfo({ optionKey }) {
  const o = optionOf(optionKey);
  if (!o) return null;
  return <StatInfo label={o.label} ariaLabel={`מה זה: ${o.label}`}>{o.info}</StatInfo>;
}

// ── Tags on a document row in the lists (/documents, the supplier page) ───────
// Desktop: coloured pills beside the doc-type badge. "כולל משוער" is for every
// role; the review marks — "לא נמצאה התאמה בחשבונית", "מחיר מהחשבונית",
// "לבדוק מע״מ" — are admin only, like the filter (owner, 2026-10-06; the server
// doesn't even send the no-match flag to other roles). For an admin "כולל
// משוער" gives way to the red tag — an unmatched note is still estimated, so
// showing both said the same thing twice.
// Phone (`phone`): the status badge stays the row's only coloured element
// (Rev 2 rows, .m-type-chip), so only the two admin tags that ask for action
// show — as neutral chips with a warning glyph. "מחיר מהחשבונית" is
// information, not an action, and the filter already answers "which ones".
export function DocPriceTags({ doc, isAdmin, phone = false }) {
  const unmatched = isAdmin && !!doc.has_unmatched_estimate;
  if (phone) {
    return (
      <>
        {unmatched ? <ReviewChip title={UNMATCHED_INFO}>לא נמצאה התאמה בחשבונית</ReviewChip> : null}
        {isAdmin && doc.vat_needs_check ? <ReviewChip title={VAT_CHECK_INFO}>לבדוק מע״מ</ReviewChip> : null}
      </>
    );
  }
  return (
    <>
      {doc.has_estimated && !unmatched ? <ListTag tone="yellow" title={ESTIMATED_DOC_INFO}>כולל משוער</ListTag> : null}
      {unmatched ? <ListTag tone="red" title={UNMATCHED_INFO}>לא נמצאה התאמה בחשבונית</ListTag> : null}
      {isAdmin && doc.has_invoice_price ? <ListTag tone="green" title={INVOICE_INFO}>מחיר מהחשבונית</ListTag> : null}
      {isAdmin && doc.vat_needs_check ? <ListTag tone="yellow" title={VAT_CHECK_INFO}>לבדוק מע״מ</ListTag> : null}
    </>
  );
}
const ListTag = ({ tone, title, children }) => (
  <span title={title} className={`badge badge-compact badge-${tone} price-list-tag`}>{children}</span>
);
const ReviewChip = ({ title, children }) => (
  <span title={title} className="m-type-chip m-review-chip"><Icon name="alert" size={11} />{children}</span>
);

// ── The VAT flag on the document screen — admin only ──────────────────────────
// The same words the admin filtered by, visibly, plus the explanation on tap
// (a title tooltip needs a hover a phone doesn't have).
export function VatCheckTag({ show }) {
  if (!show) return null;
  return (
    <span className="vat-check-tag">
      <span className="badge badge-compact badge-yellow">לבדוק מע״מ</span>
      <StatInfo label="לבדוק מע״מ" ariaLabel="לבדוק מע״מ: למה">{VAT_CHECK_INFO}</StatInfo>
    </span>
  );
}
